import { ExternalLink, Heart, Mail, MapPin, Phone } from 'lucide-react';
import logo from '../../assets/logo/logo_banco_alimentare.svg';
import { BANCO_DAUNIA_CONTACTS } from '../../lib/bancoContacts';
import { cn } from '../../lib/utils';
import SocialIcon from './SocialIcon';

function Footer({ packageJson }) {
  const {
    organization,
    legalName,
    tagline,
    address,
    phone,
    phoneHref,
    email,
    website,
    donateUrl,
    social,
  } = BANCO_DAUNIA_CONTACTS;

  return (
    <footer className="shrink-0 border-t border-[var(--ba-navbar-border)] bg-[var(--ba-navbar-bg-solid)] pb-[env(safe-area-inset-bottom,0px)] lg:mb-0">
      <div className="mx-auto max-w-6xl px-4 py-8 md:py-10">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 lg:gap-10">
          <div className="space-y-3">
            <img src={logo} alt="Banco Alimentare" className="h-12 w-auto" />
            <div>
              <p className="font-bold text-[var(--ba-body-text)]">{organization}</p>
              <p className="text-sm text-brand-green">{legalName}</p>
            </div>
            <p className="text-sm ba-muted">{tagline}</p>
            <a
              href={donateUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-orange transition-colors hover:underline"
            >
              <Heart className="h-4 w-4" />
              Sostieni il Banco Alimentare
              <ExternalLink className="h-3.5 w-3.5 opacity-70" />
            </a>
          </div>

          <div className="space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wide text-[var(--ba-muted)]">
              Contatti
            </h3>
            <ul className="space-y-2.5 text-sm">
              <li>
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-2 text-[var(--ba-body-text)] transition-colors hover:text-brand-orange"
                >
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-orange" />
                  <span>{address}</span>
                </a>
              </li>
              <li>
                <a
                  href={phoneHref}
                  className="flex items-center gap-2 text-[var(--ba-body-text)] transition-colors hover:text-brand-orange"
                >
                  <Phone className="h-4 w-4 shrink-0 text-brand-orange" />
                  <span>{phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${email}`}
                  className="flex items-center gap-2 text-[var(--ba-body-text)] transition-colors hover:text-brand-orange"
                >
                  <Mail className="h-4 w-4 shrink-0 text-brand-orange" />
                  <span className="break-all">{email}</span>
                </a>
              </li>
              <li>
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[var(--ba-body-text)] transition-colors hover:text-brand-orange"
                >
                  <ExternalLink className="h-4 w-4 shrink-0 text-brand-orange" />
                  Sito ufficiale Daunia
                </a>
              </li>
            </ul>
          </div>

          <div className="space-y-3 md:col-span-2 lg:col-span-1">
            <h3 className="text-xs font-bold uppercase tracking-wide text-[var(--ba-muted)]">
              Seguici
            </h3>
            <p className="text-sm ba-muted">
              Resta aggiornato su raccolte, eventi e iniziative sul territorio.
            </p>
            <div className="flex flex-wrap gap-2">
              {social.map((item) => (
                <a
                  key={item.id}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={item.label}
                  aria-label={item.label}
                  className={cn(
                    'inline-flex h-10 w-10 items-center justify-center rounded-lg border border-[var(--ba-surface-border)]',
                    'text-[var(--ba-navbar-link-color)] transition-all',
                    'hover:border-brand-orange/40 hover:bg-brand-orange/10 hover:text-brand-orange'
                  )}
                >
                  <SocialIcon id={item.id} />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col items-center justify-between gap-2 border-t border-[var(--ba-surface-border)] pt-6 text-center text-sm text-[var(--ba-muted)] sm:flex-row sm:text-left">
          <p>
            © {new Date().getFullYear()} {organization} — {legalName}
          </p>
          <p>
            Gestore inviti
            <span className="mx-2 hidden sm:inline">·</span>
            <span className="block sm:inline">Versione {packageJson?.version || 'N/A'}</span>
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
